import { useEffect, useState } from "react";
import { FloppyDisk, SpinnerGap } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import ImageUpload from "@/components/ImageUpload";
import { toast } from "sonner";

function Field({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-muted-foreground">{label}</span>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" />
    </label>
  );
}

function Area({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-muted-foreground">{label}</span>
      <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" />
    </label>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-3xl border border-border bg-card p-6">
      <h3 className="text-lg font-bold">{title}</h3>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">{children}</div>
    </div>
  );
}

export default function AdminSettings() {
  const { refresh, apply } = useSettings();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="py-10 text-center"><SpinnerGap size={28} className="mx-auto animate-spin text-primary" /></div>;

  const c = s.contact || {};
  const hero = s.hero || {};
  const promo = s.promo || {};
  const b = s.bank || {};
  const setC = (k, v) => setS({ ...s, contact: { ...c, [k]: v } });
  const setB = (k, v) => setS({ ...s, bank: { ...b, [k]: v } });
  const setHero = (k, v) => setS({ ...s, hero: { ...hero, [k]: v } });
  const setPromo = (k, v) => setS({ ...s, promo: { ...promo, [k]: v } });

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", s);
      apply(s);
      await refresh();
      toast.success("Settings saved — live on your site");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-settings">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Changes apply instantly across the live site.</p>
        <button onClick={save} disabled={busy} className="btn-primary !py-2.5 text-sm" data-testid="settings-save">
          {busy ? <SpinnerGap size={16} className="animate-spin" /> : <><FloppyDisk size={16} /> Save changes</>}
        </button>
      </div>

      <Section title="Branding">
        <div className="sm:col-span-2">
          <span className="text-xs font-semibold text-muted-foreground">Logo</span>
          <div className="mt-1"><ImageUpload value={s.logo_url} onUploaded={(url) => setS({ ...s, logo_url: url })} label="Logo" testid="logo-upload" /></div>
        </div>
        <label className="block">
          <span className="text-xs font-semibold text-muted-foreground">Primary brand colour</span>
          <div className="mt-1 flex items-center gap-3">
            <input type="color" value={s.brand_primary || "#EC2324"} onChange={(e) => setS({ ...s, brand_primary: e.target.value })} className="h-10 w-14 rounded-lg border border-border" data-testid="brand-color" />
            <input value={s.brand_primary || ""} onChange={(e) => setS({ ...s, brand_primary: e.target.value })} className="flex-1 rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" />
          </div>
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-muted-foreground">Secondary brand colour</span>
          <div className="mt-1 flex items-center gap-3">
            <input type="color" value={s.brand_secondary || "#333333"} onChange={(e) => setS({ ...s, brand_secondary: e.target.value })} className="h-10 w-14 rounded-lg border border-border" />
            <input value={s.brand_secondary || ""} onChange={(e) => setS({ ...s, brand_secondary: e.target.value })} className="flex-1 rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" />
          </div>
        </label>
        <div className="sm:col-span-2"><Field label="Announcement bar text" value={s.announcement} onChange={(v) => setS({ ...s, announcement: v })} /></div>
      </Section>

      <Section title="Homepage hero">
        <Field label="Badge text" value={hero.badge} onChange={(v) => setHero("badge", v)} />
        <div className="hidden sm:block" />
        <Field label="Headline line 1" value={hero.headline_1} onChange={(v) => setHero("headline_1", v)} />
        <Field label="Headline line 2 (accent)" value={hero.headline_2} onChange={(v) => setHero("headline_2", v)} />
        <div className="sm:col-span-2"><Area label="Hero subtext" value={hero.subtext} onChange={(v) => setHero("subtext", v)} /></div>
      </Section>

      <Section title="Promo banner">
        <Field label="Badge" value={promo.badge} onChange={(v) => setPromo("badge", v)} />
        <Field label="Title" value={promo.title} onChange={(v) => setPromo("title", v)} />
        <div className="sm:col-span-2"><Area label="Text" value={promo.text} onChange={(v) => setPromo("text", v)} /></div>
      </Section>

      <Section title="Contact details">
        <Field label="Email" value={c.email} onChange={(v) => setC("email", v)} />
        <Field label="Phone" value={c.phone} onChange={(v) => setC("phone", v)} />
        <Field label="WhatsApp number (digits only, e.g. 447700900123)" value={c.whatsapp} onChange={(v) => setC("whatsapp", v)} />
        <Field label="Address" value={c.address} onChange={(v) => setC("address", v)} />
        <Field label="Hours — weekdays" value={c.hours_weekday} onChange={(v) => setC("hours_weekday", v)} />
        <Field label="Hours — Saturday" value={c.hours_saturday} onChange={(v) => setC("hours_saturday", v)} />
        <Field label="Hours — Sunday" value={c.hours_sunday} onChange={(v) => setC("hours_sunday", v)} />
        <div className="hidden sm:block" />
        <Field label="Instagram URL" value={c.instagram} onChange={(v) => setC("instagram", v)} />
        <Field label="Facebook URL" value={c.facebook} onChange={(v) => setC("facebook", v)} />
        <Field label="X / Twitter URL" value={c.twitter} onChange={(v) => setC("twitter", v)} />
        <Field label="YouTube URL" value={c.youtube} onChange={(v) => setC("youtube", v)} />
      </Section>

      <Section title="Bank transfer details (included in order emails)">
        <Field label="Bank name" value={b.bank_name} onChange={(v) => setB("bank_name", v)} />
        <Field label="Account name" value={b.account_name} onChange={(v) => setB("account_name", v)} />
        <Field label="Sort code" value={b.sort_code} onChange={(v) => setB("sort_code", v)} placeholder="00-00-00" />
        <Field label="Account number" value={b.account_number} onChange={(v) => setB("account_number", v)} />
        <Field label="IBAN (optional — EU transfers)" value={b.iban} onChange={(v) => setB("iban", v)} />
        <Field label="BIC / SWIFT (optional)" value={b.bic} onChange={(v) => setB("bic", v)} />
      </Section>

      <div className="flex justify-end">
        <button onClick={save} disabled={busy} className="btn-primary !py-2.5 text-sm">
          {busy ? <SpinnerGap size={16} className="animate-spin" /> : <><FloppyDisk size={16} /> Save changes</>}
        </button>
      </div>
    </div>
  );
}
